/* "How this step works" copy for every screen (v2 pathway, October 2026). Participant-facing: no em dashes. */
export interface Guide {
  time: string;
  need?: string[];
  steps: string[];
  done?: string;
  tip?: string;
}

export const GUIDES: Record<string, Guide> = {
  hub: {
    time: "Read once, 1 min",
    steps: [
      "Tap the card marked Do this to open your next step. Each step explains exactly what to do.",
      "Work through the GIG steps in order. Steps 1 to 4 make you Work-Enabled. Step 5, your certificate, completes the pathway and opens the Gig Board.",
      "Move the FINANCE steps along at the same time. They're short checklists that prepare you for the financing provider.",
      "Everything saves automatically. Sign in again with the same email and ID to pick up where you left off.",
    ],
    done: "You know which card to open next: it's the one marked Do this.",
  },
  audit: {
    time: "3 min",
    steps: [
      "Answer the five questions honestly. There are no wrong answers.",
      "Tap See my score. You get a starting score and tips on where to focus.",
      "Come back after Step 4 and see how far you've moved.",
    ],
    done: "You see your starting score.",
  },
  gig1: {
    time: "20 to 40 min",
    need: [
      "A phone or laptop with internet",
      "A government ID for the platform's identity check (KYC)",
      "A clear photo of yourself for your profile",
    ],
    steps: [
      "Write one line about who you help and what you do. The checks under the box turn green when it's clear.",
      "Pick a platform below and tap Sign up. Use our link so your progress can be tracked.",
      "Complete your profile fully (photo, headline, skills) and start the platform's identity check (KYC).",
      "Open your own profile, copy the link from the browser's address bar, paste it into the box and tap Submit.",
      "Repeat until you're registered on 5 platforms. Each link must be your profile page on that platform.",
      "When your niche line and 5 platforms show green, tap Complete Step 1.",
    ],
    done: "Your niche line passes all 3 checks and 5 platforms show Registered.",
    tip: "Take a screenshot of your KYC status page on the platform. You'll upload it in Step 4.",
  },
  gig2: {
    time: "1 to 3 hours (the portfolio takes longest)",
    need: [
      "At least 3 pieces of your work (real or practice projects)",
      "Your CV, or 20 minutes to make one with a free builder",
    ],
    steps: [
      "Study the example portfolio. Each number matches one of the 10 things clients look for.",
      "Build or update your portfolio on one of the accepted sites. Paste the link and tap Verify.",
      "Optional: check yours against the 10 points clients look for first.",
      "Upload your CV as a PDF or Word file.",
      "When your portfolio shows Verified and your CV is on file, tap Complete Step 2.",
    ],
    done: "Your portfolio shows Verified and your CV is on file.",
    tip: "Your portfolio must open without a login or download. Test the link in a private browser window first.",
  },
  gig3: {
    time: "2 to 4 hours, over a few sessions",
    need: ["Your portfolio link from Step 2, to use as proof in your bids"],
    steps: [
      "Browse the jobs. Use search and the filters to find ones that fit your niche.",
      "Tap a job to open it and write a bid. Your first 2 bids show a model proposal to learn from.",
      "Submit. You'll see a score and exactly what to fix. A bid that doesn't count can be edited and resubmitted.",
      "Keep going until 10 bids are counted, then tap Complete Step 3.",
    ],
    done: "10 bids counted.",
    tip: "Every winning bid makes 6 moves: the client's name, their need, your proof, your plan, your price, and a question.",
  },
  bid: {
    time: "10 to 15 min per bid",
    steps: [
      "Read the brief. Note the client's name and exactly what they need.",
      "If a model proposal is shown, read it and the notes on why it works. Don't copy it.",
      "Enter your price, inside the client's budget.",
      "Write 60 to 180 words: greet the client by name, name their need, link to similar work, explain your plan in 2 or 3 steps, give your price and timeline, and end with a question.",
      "Tap Submit bid. Fix anything marked with a red cross and submit again.",
    ],
    done: 'You see a green "Counted" message.',
    tip: "Never invent clients or results. Practice projects are fine as proof.",
  },
  gig4: {
    time: "About 30 minutes",
    need: [
      "A payment account you've opened and verified (for example Payoneer or Grey)",
      "A screenshot of your KYC status page on one of your platforms",
    ],
    steps: [
      "Part 1: select every payment method you've set up, then confirm your account is verified.",
      "Part 2: choose your KYC status on a platform and upload a screenshot of the status page.",
      "When both parts show Done, tap Confirm Work-Enabled to get your Launch Plan.",
    ],
    done: "Payment set up and KYC screenshot uploaded.",
    tip: 'KYC still pending on a platform? "Submitted, awaiting approval" counts. Upload the screenshot now and carry on.',
  },
  launch: {
    time: "5 min",
    steps: [
      "Read it top to bottom and check your details are right.",
      "Tap Print or save as PDF and keep a copy on your phone.",
      'Start the "next 7 days" list today.',
      "Then upload your Growth Pathway certificate in Step 5 to open the Gig Board.",
    ],
  },
  gig5: {
    time: "5 min",
    need: ["Your Growth Pathway certificate from the LMS: a PDF, or a photo or screenshot"],
    steps: [
      "Download your certificate from the LMS, or take a clear photo or screenshot of it.",
      "Tap Upload my certificate and choose the file. A PDF must be 2.5 MB or smaller.",
      "Tap Complete the Growth Pathway. The Gig Board opens straight away.",
    ],
    done: "Your certificate shows Uploaded and the Gig Board is open.",
  },
  fin1: {
    time: "2 min",
    steps: [
      "Tap every service you'd like to be considered for. You can pick more than one.",
      "Tap Complete Finance Step 1. You can come back and change your choice later.",
    ],
    done: "At least one service is selected.",
  },
  fin2: {
    time: "5 min here, plus time to gather documents",
    steps: [
      "Read each item and the tip under it.",
      "Gather anything you don't have yet. Don't type numbers or upload anything here.",
      "Check off each item once you actually have it ready.",
      "When every item is checked, tap Complete.",
    ],
    done: "Every item is checked.",
    tip: "Your name must be spelled the same way on your ID, BVN and bank account.",
  },
  fin3: {
    time: "5 min here, plus time to gather records",
    steps: [
      "Read each item and the tip under it.",
      "Gather your records. Don't upload anything here.",
      "Check off each item once you have it ready. If you chose device financing, that includes 10% savings and your guarantor's documents.",
      "When every item is checked, tap Complete.",
    ],
    done: "Every item is checked.",
    tip: "Download your 6 months of bank statements early (some banks take a few days), and ask your guarantor early too.",
  },
  fin4: {
    time: "2 min",
    steps: [
      "Answer the 4 questions honestly. Only your readiness level is saved.",
      "Read the fix for anything that's flagged.",
      "Tap Save my result. Come back and update it after you fix things.",
    ],
    done: "All 4 questions are answered and your result is saved.",
    tip: "You need at least Nearly Ready to be referred in Finance Step 6.",
  },
  fin5: {
    time: "Completes with gig Step 5",
    steps: [
      "Finish gig Step 5 (upload your certificate).",
      "Check the box once your LMS modules up to Step 5 are done.",
      "Tap Complete Finance Step 5.",
    ],
    done: "Gig Step 5 is done and the box is checked.",
  },
  fin6: {
    time: "Automatic",
    steps: [
      "This page checks three things for you: Finance Steps 1 to 5, gig Step 5 (your certificate), and your readiness level.",
      "Fix anything with a red cross by following its note.",
      "Once all three are green, you're referred and your provider will contact you.",
    ],
    done: 'The heading says "Referred to Your Provider".',
  },
};
